import { type UploadGeoJsonResponse, useUploadGeoJson } from "@/api/hooks/customGeojson";
import { FileUploader } from "@/overlay/components/CommonParts/FileUploader";
import { ErrorMsgBox, SuccessMsgBox, WarningMsgBox } from "@/overlay/components/CommonParts/UserMsgBox";
import { UploadFormat } from "@/overlay/components/FileWindow/utils";
import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import Logger from "@mm/logging";
import { type ReactNode, useState } from "react";
import styles from "./style.module.scss";

const logger = Logger.fromFilename(__filename);

const SINGLE_GEOJSON_FILE_SIZE_MAX_MB = 5;
const MAX_SIZE_BYTES = SINGLE_GEOJSON_FILE_SIZE_MAX_MB * 1024 * 1024;

// we use the filename as new file label. Label can be change with a input field in the file manager (will be added later)
const MAX_FILENAME_LENGTH = 50;
const CUSTOM_GEOJSON_SMALL_POLYGON_WARNING_CODE = "custom_geojson.small_polygon";

const SAFE_ASCII_FILE_LABEL_REGEX = /^[a-zA-Z0-9 _-]+$/;

function isValidLabel(label: string): boolean {
  return SAFE_ASCII_FILE_LABEL_REGEX.test(label.trim());
}
function isFilenameSafeFrontend(filename: string): boolean {
  if (filename.includes("/") || filename.includes("\\") || filename.includes("..")) {
    return false;
  }

  if (filename.length > MAX_FILENAME_LENGTH) {
    return false;
  }

  return isValidLabel(filename);
}

function translateUploadWarningCode(code: string): string | null {
  switch (code) {
    case CUSTOM_GEOJSON_SMALL_POLYGON_WARNING_CODE:
      return t`Some polygons are too small to return a value. You can still use them as a static asset in your visualisation, but they will return no value.`;
    default:
      return null;
  }
}

function getUploadWarnings(uploadedFile?: Pick<UploadGeoJsonResponse, "warning_codes" | "warnings">): string[] {
  if (!uploadedFile) {
    return [];
  }

  if (uploadedFile.warning_codes?.length) {
    const translatedWarnings = uploadedFile.warning_codes
      .map((code) => translateUploadWarningCode(code))
      .filter((warning): warning is string => Boolean(warning));

    if (translatedWarnings.length) {
      return translatedWarnings;
    }
  }

  return uploadedFile.warnings ?? [];
}

export function GeoJSONUploadSection() {
  const [fileName, setFileName] = useState("");
  const [userErrs, setUserErrs] = useState<{ userMsg: string | string[] | ReactNode }[]>([]);
  const { mutate: uploadGeoJson, isLoading, isSuccess, data: uploadedFile } = useUploadGeoJson();
  const successFileName = uploadedFile?.label ?? fileName;
  const uploadWarnings = getUploadWarnings(uploadedFile);

  function importGeoJSON(file: File, event: ProgressEvent<FileReader>): Promise<void> {
    const fileName = file.name;
    const baseName = fileName.replace(/\.[^/.]+$/, "");
    setFileName(fileName);
    if (baseName.length > MAX_FILENAME_LENGTH) {
      setUserErrs((errs) => [
        ...errs,
        {
          userMsg: (
            <CustomErrorMsg
              text={t`The filename "${fileName}" is too long. Please use a shorter name (max. ${MAX_FILENAME_LENGTH} characters).`}
            />
          ),
        },
      ]);
      return Promise.resolve();
    }

    if (!isFilenameSafeFrontend(baseName)) {
      setUserErrs((errs) => [
        ...errs,
        { userMsg: <ImportErrorMsg fileName={fileName} uploadFormat={UploadFormat.GeoJSON} /> },
      ]);
      return Promise.resolve();
    }

    const contentStr = event.target?.result;
    if (!contentStr) {
      setUserErrs((errs) => [
        ...errs,
        { userMsg: <ImportErrorMsg fileName={fileName} uploadFormat={UploadFormat.GeoJSON} /> },
      ]);
      return Promise.resolve();
    }
    // Bytes
    if (file.size > MAX_SIZE_BYTES) {
      setUserErrs((errs) => [
        ...errs,
        {
          userMsg: (
            <CustomErrorMsg
              text={t`File "${fileName}" exceeds the maximum size of ${SINGLE_GEOJSON_FILE_SIZE_MAX_MB} MB.`}
            />
          ),
        },
      ]);
      return Promise.resolve();
    }

    try {
      JSON.parse(contentStr as string);
    } catch {
      setUserErrs((errs) => [
        ...errs,
        {
          userMsg: t`File "${fileName.toString()}" could not be loaded. Please make sure it's a valid ${UploadFormat.JSON.toString()} file.`,
        },
      ]);
      return Promise.resolve();
    }

    // additional checks

    return new Promise((resolve) => {
      uploadGeoJson(
        { fileName: baseName, file },
        {
          onSuccess: () => {
            resolve();
          },
          onError: () => {
            setUserErrs((errs) => [
              ...errs,
              {
                userMsg: <ImportErrorMsg fileName={fileName} uploadFormat={UploadFormat.GeoJSON} />,
              },
            ]);
            resolve();
          },
        },
      );
    });
  }

  // Currently can only one file be uploaded
  function loadFiles(files: FileList) {
    setFileName("");
    if (files.length === 0) {
      return;
    }

    setUserErrs([]);
    const allRequests: Promise<void>[] = [];
    for (let i = 0; i < files.length; i++) {
      const reader = new FileReader();
      reader.readAsText(files[i], "UTF-8");
      reader.onload = (event: ProgressEvent<FileReader>) => {
        allRequests.push(importGeoJSON(files[i], event));
      };
      reader.onerror = () => fileLoadErrHandler(files[i].name);
    }
    Promise.all(allRequests);
  }

  function fileLoadErrHandler(fileName: string) {
    logger.error(`Unknown error occurred while loading file "${fileName}" (FileReaderError).`);
    setUserErrs((errs) => [
      ...errs,
      { userMsg: <ImportErrorMsg fileName={fileName} uploadFormat={UploadFormat.GeoJSON} /> },
    ]);
  }

  return (
    <div className={classNames(styles.geojson__section, styles.geojson__upload)}>
      <FileUploader
        allowMultiple={false}
        onSubmit={(files) => loadFiles(files)}
        isUploading={isLoading}
        accept=".geojson"
      />

      <div className={styles["geojson__upload-hint"]}>
        <div>{t`Supported file format for the upload is .geojson`}</div>
        <div>{t`Maximum file upload size is ${SINGLE_GEOJSON_FILE_SIZE_MAX_MB} MB`}</div>
      </div>
      <div className={styles["user-message-section"]}>
        {!(userErrs.length > 0) && isSuccess && successFileName && (
          <>
            <SuccessMsgBox msg={ImportSuccessMsg({ fileName: successFileName })} />
            {uploadWarnings.length ? <WarningMsgBox msg={UploadWarningMsg(uploadWarnings)} /> : null}
          </>
        )}
        {userErrs.length > 0 && userErrs.map((err) => <ErrorMsgBox key={err.userMsg?.toString()} msg={err.userMsg} />)}
      </div>
    </div>
  );
}

const UploadWarningMsg = (warnings: string[]) => {
  return (
    <div className={styles.msg__warning}>
      <span className={classNames("material-icons", styles.msg__warning__icon)}>warning</span>
      <span>{warnings.join()}</span>
    </div>
  );
};

const ImportSuccessMsg = ({ fileName }: { fileName: string }) => {
  return (
    <div className={styles.msg__success}>
      <span className={classNames("material-icons", styles.msg__success__icon)}>check_circle</span>
      <span>{t`File “${fileName}” successfully imported.`}</span>
    </div>
  );
};

const CustomErrorMsg = ({ text }: { text: string }) => {
  return (
    <div className={styles.msg__error}>
      <span className={classNames("material-icons", styles.msg__error__icon)}>cancel</span>
      <span>{text}</span>
    </div>
  );
};

const ImportErrorMsg = ({ fileName, uploadFormat }: { fileName: string; uploadFormat: UploadFormat }) => {
  return (
    <div className={styles.msg__error}>
      <span className={classNames("material-icons", styles.msg__error__icon)}>cancel</span>
      <span>
        {t`File "${fileName}" could not be imported.`}
        <br />
        {t`Please make sure it's a valid ${uploadFormat} file.`}
      </span>
    </div>
  );
};
